/**
 * SupabaseStorageProvider.ts — the cloud provider (SUPABASE FOUNDATION™).
 *
 * IMPORTANT BOUNDARY: the browser NEVER talks to Supabase directly. All
 * operations go to the api-server's /api/persistence routes; the service-role
 * key lives exclusively server-side. This class is "the Supabase provider"
 * from the client's perspective — connection management, retry, latency and
 * health reporting for the cloud persistence path.
 */

import type { CloudDomain, ProviderHealth, StorageProvider, StoredDocument } from './StorageProvider';
import { supabase } from '@/lib/supabaseClient';

const RETRY_ATTEMPTS = 2; // total tries = 1 + RETRY_ATTEMPTS
const RETRY_DELAY_MS = 750;

function apiBase(): string {
  const base = (import.meta as any).env?.BASE_URL ?? '/';
  return `${base}api`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Returns an Authorization: Bearer header when a Supabase session is active,
 * otherwise returns an empty object so cookie-session users are unaffected.
 *
 * WHY: SupabaseStorageProvider sends requests to /api/persistence which goes
 * through requireAuth. Supabase users have no nomad_session cookie — the only
 * credential they carry is a JWT. Without this header every persistence call
 * returns 401, meaning persona / workspace data is never saved or restored,
 * and the onboarding wizard repeats on every login.
 */
async function authHeaders(): Promise<Record<string, string>> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) return { Authorization: `Bearer ${token}` };
  } catch { /* best-effort — fall back to cookie-only */ }
  return {};
}

export class SupabaseStorageProvider implements StorageProvider {
  readonly id = 'supabase';
  readonly remote = true;

  private health: ProviderHealth = {
    id: this.id,
    reachable: false,
    lastLatencyMs: null,
    lastSuccessAt: null,
    consecutiveFailures: 0,
    detail: 'not yet probed',
  };
  private retryCount = 0;

  /** Total retries performed since boot (Dev Center diagnostic). */
  getRetryCount(): number {
    return this.retryCount;
  }

  getHealth(): ProviderHealth {
    return { ...this.health };
  }

  async checkHealth(): Promise<ProviderHealth> {
    const started = Date.now();
    try {
      const hdrs = await authHeaders();
      const res = await fetch(`${apiBase()}/persistence/status`, {
        credentials: 'include',
        headers: hdrs,
      });
      const body = res.ok ? await res.json() : null;
      const ready = !!body?.configured && !!body?.tableReady;
      this.recordSuccessOrFailure(ready, Date.now() - started,
        ready ? null : (body ? `configured=${body.configured} tableReady=${body.tableReady} ${body.detail ?? ''}`.trim() : `status ${res.status}`));
    } catch (err) {
      this.recordSuccessOrFailure(false, null, err instanceof Error ? err.message : 'network error');
    }
    return this.getHealth();
  }

  async read<T>(domain: CloudDomain, key: string): Promise<StoredDocument<T> | null> {
    const res = await this.withRetry(async () => {
      const hdrs = await authHeaders();
      return fetch(`${apiBase()}/persistence/${domain}/${encodeURIComponent(key)}`, {
        credentials: 'include',
        headers: hdrs,
      });
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`read ${domain}/${key} → ${res.status}`);
    const doc = (await res.json()) as { payload: T; updatedAt: string };
    return { payload: doc.payload, updatedAt: doc.updatedAt };
  }

  async write(domain: CloudDomain, key: string, payload: unknown, updatedAt: string): Promise<void> {
    const res = await this.withRetry(async () => {
      const hdrs = await authHeaders();
      return fetch(`${apiBase()}/persistence/${domain}/${encodeURIComponent(key)}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...hdrs },
        body: JSON.stringify({ payload, updatedAt }),
      });
    });
    // RC-02 (Build 2.21.0): 409 Conflict means the server has a strictly newer
    // record — the local write would overwrite unseen data. Treat as "server
    // wins": log a warning and do NOT throw, so the write is not re-queued for
    // infinite retry. The hydrate() path will bring down the server version on
    // the next boot.
    if (res.status === 409) {
      console.warn(`[StorageManager] write ${domain}/${key} — server has newer record (409), accepting server version.`);
      return;
    }
    if (!res.ok) throw new Error(`write ${domain}/${key} → ${res.status}`);
  }

  async delete(domain: CloudDomain, key: string): Promise<void> {
    const res = await this.withRetry(async () => {
      const hdrs = await authHeaders();
      return fetch(`${apiBase()}/persistence/${domain}/${encodeURIComponent(key)}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: hdrs,
      });
    });
    if (!res.ok) throw new Error(`delete ${domain}/${key} → ${res.status}`);
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  /** Retry transient failures (network errors / 5xx). 4xx returns immediately. */
  private async withRetry(op: () => Promise<Response>): Promise<Response> {
    const started = Date.now();
    let lastError: unknown = null;
    for (let attempt = 0; attempt <= RETRY_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        this.retryCount += 1;
        await sleep(RETRY_DELAY_MS * attempt);
      }
      try {
        const res = await op();
        if (res.status >= 500) {
          lastError = new Error(`server ${res.status}`);
          continue;
        }
        this.recordSuccessOrFailure(true, Date.now() - started, null);
        return res;
      } catch (err) {
        lastError = err;
      }
    }
    this.recordSuccessOrFailure(false, null, lastError instanceof Error ? lastError.message : 'network error');
    throw lastError instanceof Error ? lastError : new Error('cloud request failed');
  }

  private recordSuccessOrFailure(ok: boolean, latencyMs: number | null, detail: string | null): void {
    if (ok) {
      this.health = {
        id: this.id,
        reachable: true,
        lastLatencyMs: latencyMs,
        lastSuccessAt: new Date().toISOString(),
        consecutiveFailures: 0,
        detail: null,
      };
    } else {
      this.health = {
        ...this.health,
        reachable: false,
        consecutiveFailures: this.health.consecutiveFailures + 1,
        detail,
      };
    }
  }
}
